import { useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import InlineEditSelect, { type InlineOption } from "./InlineEditSelect";

function initials(name: string) {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

/**
 * Escolha do responsável pela oportunidade.
 *
 * A lista vem de `crm.sellers.list`, que só devolve usuários COM perfil de
 * vendedor. Quando o responsável atual não tem perfil (foi atribuído antes de
 * virar vendedor, ou o perfil foi removido), ele é anexado à lista — senão o
 * valor vigente simplesmente não aparece e a interface sugere que a
 * oportunidade está sem dono.
 *
 * A query só dispara quando o popover abre: uma tabela com 500 linhas não pode
 * fazer 500 requisições de vendedores.
 */
export default function InlineEditUser({
  value,
  currentUserName,
  onCommit,
  canEdit,
  label = "responsável",
  children,
  className,
  disabledReason,
  fill = false,
}: {
  value: number | null;
  /** Nome do responsável atual, para o fallback descrito acima. */
  currentUserName?: string | null;
  /** `name` é o rótulo escolhido, para o update otimista de quem consome. */
  onCommit: (next: number | null, name?: string | null) => Promise<void>;
  canEdit: boolean;
  label?: string;
  children: React.ReactNode;
  className?: string;
  disabledReason?: string;
  /** Ocupa a largura do container — ver o `fill` do InlineEditShell. */
  fill?: boolean;
}) {
  const [opened, setOpened] = useState(false);

  const sellersQuery = trpc.crm.sellers.list.useQuery(undefined, {
    enabled: opened,
    staleTime: 5 * 60_000,
  });

  const options = useMemo<InlineOption<number | null>[]>(() => {
    const rows = sellersQuery.data ?? [];
    const opts: InlineOption<number | null>[] = rows.map((s: any) => {
      const name = s.user.name ?? s.user.email ?? `Vendedor #${s.profile.userId}`;
      return {
        value: s.profile.userId as number,
        label: name,
        keywords: s.user.email ?? "",
        node: (
          <span className="flex min-w-0 items-center gap-2">
            <Avatar className="h-5 w-5 shrink-0">
              <AvatarFallback className="bg-primary/10 text-[9px] font-semibold text-primary">
                {initials(name)}
              </AvatarFallback>
            </Avatar>
            <span className="truncate">{name}</span>
          </span>
        ),
      };
    });

    // Responsável atual que não está na lista de vendedores.
    if (value != null && !opts.some((o) => o.value === value)) {
      const name = currentUserName ?? `Usuário #${value}`;
      opts.unshift({ value, label: name, node: <span className="truncate">{name}</span> });
    }

    opts.push({
      value: null,
      label: "Não atribuído",
      node: <span className="text-muted-foreground">Não atribuído</span>,
    });
    return opts;
  }, [sellersQuery.data, value, currentUserName]);

  return (
    <div onPointerEnter={() => setOpened(true)} onFocus={() => setOpened(true)}>
      <InlineEditSelect<number | null>
        value={value}
        options={options}
        onCommit={(next, option) => onCommit(next, next == null ? null : option?.label)}
        canEdit={canEdit}
        label={label}
        className={className}
        fill={fill}
        searchPlaceholder="Buscar vendedor..."
        emptyMessage="Nenhum vendedor cadastrado"
        loading={opened && sellersQuery.isLoading}
        disabledReason={disabledReason}
      >
        {children}
      </InlineEditSelect>
    </div>
  );
}
